import {makeAutoObservable} from 'mobx';

import type {AudioModel} from '../../../../../../core/models/Audio/Audio.model';
import type {AudioModelFactory} from '../../../../../../core/models/Audio/AudioModel.factory';
import type {VoiceAttachmentStateOptions} from './types';

import {emitter} from '../../../../../../utils/eventEmitter';
import {VoicePlaybackController} from './VoicePlaybackController';

export class VoiceAttachmentState {
  model: AudioModel;

  constructor(
    public id: string,
    private options: VoiceAttachmentStateOptions = {
      useQueue: true,
    },
    private audioModelFactory: AudioModelFactory,
  ) {
    this.onPlay = this.onPlay.bind(this);
    this.onPause = this.onPause.bind(this);

    this.model = this.audioModelFactory.create({
      id,
      onEnded: this.onEndedHandler.bind(this),
      onPause: this.onPauseHandler.bind(this),
      onPlay: this.onPlayHandler.bind(this),
    });

    makeAutoObservable(
      this,
      {},
      {
        autoBind: true,
      },
    );

    this.setup();
  }

  destruct() {
    this.model.destruct();

    emitter.off(VoicePlaybackController.PLAY_EVENT_KEY, this.onPlay);
    emitter.off(VoicePlaybackController.PAUSE_EVENT_KEY, this.onPause);
  }

  get PROGRESS_BAR_STEP() {
    return 0.01;
  }

  async play() {
    if (!this.model.isLoaded) {
      return;
    }

    VoicePlaybackController.emitCurrentVoiceMessageId(this.id);

    return this.model.play();
  }

  private onEndedHandler() {
    if (this.options.useQueue) {
      VoicePlaybackController.emitPlayNext(this.id);
    }
  }

  private onPause(id: string) {
    if (this.id === id) {
      Promise.resolve().then(() => {
        this.model.pause();
      });
    }
  }

  private onPauseHandler() {}

  private onPlay(id: string) {
    if (this.id === id) {
      Promise.resolve().then(() => {
        this.play();
      });
    }
  }

  private onPlayHandler() {
    if (this.options.useQueue) {
      VoicePlaybackController.emitCurrentVoiceMessageId(this.id);
    }
  }

  private setup() {
    emitter.on(VoicePlaybackController.PLAY_EVENT_KEY, this.onPlay);
    emitter.on(VoicePlaybackController.PAUSE_EVENT_KEY, this.onPause);
  }
}
